import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const API = process.env.REACT_APP_API_URL || "http://localhost:8080";

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    window.scrollTo({ top: 0, behavior: "instant" });
    fetch(`${API}/?url=blog/post/${slug}&_t=${Date.now()}`)
      .then((r) => {
        if (r.status === 404) { setNotFound(true); return null; }
        return r.json();
      })
      .then((data) => { if (data) setPost(data); });
  }, [slug]);

  if (notFound) {
    return (
      <div className="container py-5 text-center">
        <h2>Post Not Found</h2>
        <Link to="/blog" className="btn btn-dark mt-3">Back to Blog</Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-secondary" />
      </div>
    );
  }

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" });

  return (
    <>
      <Helmet>
        <title>{post.meta_title || post.title} | Clear &amp; Clean</title>
        <meta name="description" content={post.meta_description || post.excerpt} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://www.clearandclean.co.nz/blog/${slug}`} />
        <meta property="og:title" content={post.meta_title || post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt} />
        {post.featured_image && <meta property="og:image" content={post.featured_image} />}
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.excerpt,
          "author": { "@type": "Organization", "name": post.author || "Clear & Clean" },
          "publisher": { "@type": "Organization", "name": "Clear & Clean", "url": "https://www.clearandclean.co.nz" },
          "datePublished": post.created_at,
          "dateModified": post.updated_at || post.created_at,
          "url": `https://www.clearandclean.co.nz/blog/${slug}`,
          ...(post.featured_image ? { "image": post.featured_image } : {}),
        })}</script>
      </Helmet>

      {/* Hero */}
      <section
        className="hero-wrap hero-wrap-2"
        style={{ backgroundImage: post.featured_image ? `url("${post.featured_image}")` : 'url("/images/service_2.jpg")' }}
        data-stellar-background-ratio="0.5"
      >
        <div className="overlay" />
        <div className="container">
          <div className="row no-gutters slider-text align-items-end">
            <div className="col-md-9 pb-5">
              <p className="breadcrumbs mb-2">
                <span className="mr-2"><Link to="/">Home <i className="fa fa-chevron-right" /></Link></span>
                <span className="mr-2"><Link to="/blog">Blog <i className="fa fa-chevron-right" /></Link></span>
                <span>{post.title} <i className="fa fa-chevron-right" /></span>
              </p>
              <h1 className="mb-0 bread">{post.title}</h1>
            </div>
          </div>
        </div>
      </section>

      {/* Article */}
      <section className="ftco-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">

              {/* Meta bar */}
              <div style={{
                display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap",
                borderBottom: "1px solid #e5e7eb", paddingBottom: 16, marginBottom: 32,
                color: "#6b7280", fontSize: 14,
              }}>
                <span><i className="fa fa-user mr-2" />{post.author}</span>
                <span><i className="fa fa-calendar mr-2" />{formatDate(post.created_at)}</span>
                {post.updated_at && post.updated_at !== post.created_at && (
                  <span><i className="fa fa-refresh mr-2" />Updated {formatDate(post.updated_at)}</span>
                )}
              </div>

              {/* Content */}
              {post.content ? (
                <div
                  className="blog-post-content"
                  style={{ lineHeight: 1.85, fontSize: 15, color: "#374151" }}
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />
              ) : (
                <p className="lead text-muted text-center py-5">{post.excerpt}</p>
              )}

              {/* Back link */}
              <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 40, paddingTop: 24 }}>
                <Link to="/blog" className="btn btn-outline-dark btn-sm">
                  <i className="fa fa-arrow-left mr-2" />Back to Blog
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogPost;
